import { z } from "zod";
import type { OptionCategoryType } from "../api/optionCategories";
import type { PartnerRole } from "../api/partners";

export type Form = { id: string; name: string; sections: Section[]; sectionGroups?: SectionGroup[] };
export type SectionGroup = { id: string; name: string };
export type Section = {
  id: string;
  name: string;
  questionCards: QuestionCard[];
  sectionGroupId?: SectionGroup["id"];
  description?: string;
};

export type QuestionCard<Input extends _Input = _Input> = {
  id: string;
  header: string;
  questionText?: string;
  description?: string;
  helpInformation?: string;
  extraHelpInformation?: string;
  readonly?: boolean;
  followUps?: FollowUp[];
  input: Input;
  mandatory?: boolean;
  tag?: string;
};

type _Input =
  | MultiSelectInput
  | MeasureInput
  | DynamicMultiSelectInput
  | SingleSelectInput
  | TextInput
  | SystemInput
  | EmailInput
  | PhoneNumberInput
  | PersonInput
  | YesOrNoInput
  | CountryInput
  | MultiplePersonInput
  | DateInput
  | DpiaInput
  | MultipleTenantInput
  | MultipleExternalLinkInput
  | ExternalLinkInput
  | MultipleContactPersonInput
  | MultiplePartnerInput
  | MultipleSubSupplierInput;

type MultiSelectInput = { type: "multipleChoiceInput"; options: Option[] };
type DynamicMultiSelectInput = {
  type: "dynamicMultipleChoiceInput";
  categoryType: OptionCategoryType;
};
export type CountryInput = { type: "countryInput"; options: Option[] };
export type SingleSelectInput = { type: "singleChoiceInput"; options: Option[] };
export type TextInput = { type: "textInput"; numberOfRows?: number };
type SystemInput = { type: "systemInput" };
type MeasureInput = { type: "measureInput"; options: Option[] };
type EmailInput = { type: "emailInput" };
type PhoneNumberInput = { type: "phoneNumberInput" };
type PersonInput = { type: "personInput" };
type MultiplePersonInput = { type: "multiplePersonInput" };
type DateInput = { type: "dateInput" };
type DpiaInput = { type: "singleDpiaInput" };
type MultipleTenantInput = { type: "multipleTenantInput" };
type ExternalLinkInput = { type: "externalLinkInput" };
type MultipleExternalLinkInput = { type: "multipleExternalLinkInput" };
type MultipleContactPersonInput = { type: "multipleContactPersonInput" };
type MultiplePartnerInput = { type: "multiplePartnerInput"; header: string; partnerRole?: PartnerRole };
export type MultipleSubSupplierInput = {
  type: "multipleSubSupplierInput";
  countryOptions: { label: string; value: string }[];
};

export type YesOrNoInput = { type: "yesOrNoInput"; yesDescription?: string; noDescription?: string };

export type InputType = QuestionCard["input"]["type"];
export type FollowUp = { questionCard: QuestionCard; rule: { parentValue: Answer["value"]; operator: "eq" | "any" } };
export type Option = { label: string; value: string; description?: string };

export type SaveInput = (answer: AnswerRequest, options?: { rollbackOnError?: boolean }) => Promise<unknown>;
export type OnSave = (answer: AnswerRequest) => Promise<unknown>;

export type Answer = WithNullableValue<{ questionId: string } & _Answer>;

type _Answer =
  | { type: MultiSelectInput["type"]; value: string[] }
  | { type: DynamicMultiSelectInput["type"]; value: Array<LabelAndValue> }
  | { type: CountryInput["type"]; value: string[] }
  | { type: SingleSelectInput["type"]; value: string }
  | { type: TextInput["type"]; value: string }
  | { type: SystemInput["type"]; value: string[] }
  | { type: MeasureInput["type"]; value: string[] }
  | { type: EmailInput["type"]; value: string }
  | { type: PhoneNumberInput["type"]; value: string }
  | { type: PersonInput["type"]; value: string }
  | { type: YesOrNoInput["type"]; value: "yes" | "no" }
  | { type: MultiplePersonInput["type"]; value: string[] }
  | { type: DateInput["type"]; value: string }
  | { type: DpiaInput["type"]; value: string }
  | { type: MultipleTenantInput["type"]; value: string[] }
  | { type: MultipleExternalLinkInput["type"]; value: MultipleExternalLinkValue }
  | { type: ExternalLinkInput["type"]; value: string }
  | { type: MultipleContactPersonInput["type"]; value: MultipleContactPersonValue }
  | { type: MultiplePartnerInput["type"]; value: MultiplePartnerValue }
  | { type: MultipleSubSupplierInput["type"]; value: MultipleSubSupplierValue };

export type MultipleContactPersonValue = Array<{
  firstName?: string | undefined;
  lastName?: string | undefined;
  email?: string | undefined;
  phoneNumber?: string | undefined;
  title?: string | undefined;
}>;

export type MultiplePartnerValue = Array<{
  id: string;
  type: "partner" | "personalDataController" | "customerOrganisation";
}>;

export type MultiplePartnerType = MultiplePartnerValue[number]["type"];

export type MultipleExternalLinkValue = Array<{ label: string; url: string }>;

export const MultipleSubSupplierValueSchema = z.array(
  z.object({
    name: z.string(),
    organisationNumber: z.string().optional(),
    country: z.string().optional(),
  })
);

export type MultipleSubSupplierValue = z.infer<typeof MultipleSubSupplierValueSchema>;

type LabelAndValue = { label: string; value: string };
const isLabelAndValue = (value: unknown): value is LabelAndValue =>
  typeof value === "object" && value !== null && "label" in value && "value" in value;

export const isLabelAndValueArray = (value: unknown): value is LabelAndValue[] =>
  Array.isArray(value) && value.every(isLabelAndValue);

/** Most input types has the same answer response as answer request but the request value is allowed to be null in order to remove the answer */
type WithNullableValue<A extends { value: unknown }> = {
  [Key in keyof A]: Key extends "value" ? A[Key] | null : A[Key];
};

export type AnswerRequest = WithNullableValue<
  | Exclude<Answer, { type: DynamicMultiSelectInput["type"] }>
  | { questionId: string; type: DynamicMultiSelectInput["type"]; value: string[] }
>;

export const answerToAnswerRequest = (answer: Answer): AnswerRequest => {
  if (answer.type === "dynamicMultipleChoiceInput") {
    return {
      questionId: answer.questionId,
      type: answer.type,
      value: answer.value ? answer.value.map(({ value }) => value) : null,
    };
  }

  return answer;
};

/** AI-generated: Asserts that T has exactly the same union members as InputType. */
export function isIncludingAllInputTypes<T>(
  ..._args: [Exclude<T, InputType>] extends [never]
    ? [Exclude<InputType, T>] extends [never]
      ? []
      : ["❌ T is missing members from InputType:", Exclude<InputType, T>]
    : ["❌ T has extra members not in InputType:", Exclude<T, InputType>]
): void {
  _args;
}
